/* eslint-disable no-console */
import { useEffect, useRef } from 'react';
import _ from 'lodash';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import * as evaluateExpression from '@/helpers/evaluateExpression';
import trackEvents from '@/helpers/trackEvents';

type BaseFieldTemplate =
  & Pick<QuestionnaireApiModels.FieldTemplate,
    | 'key'
    | 'label'
    | 'rank'
    | 'type'
    | 'subsection'
    | 'listFieldTemplateId'
    | 'autofill'
    | 'conditionExpression'
  >
  & {
    listItemTemplates?: Array<Pick<QuestionnaireApiModels.FieldTemplate, 'key'>>;
  };

type ResponseSources = Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']>;

type Props = {
  fieldsByKey: Record<
    string,
    BaseFieldTemplate
  >;
  formMethods: {
    getValues: (key?: string) => unknown;
  }
  responseSourcesRef: { current: ResponseSources };
  sections: Array<
    & Pick<QuestionnaireApiModels.SectionTemplate,
      | 'key'
      | 'label'
      | 'rank'
      | 'subsections'
    >
    & {
      fields: Array<
        & Pick<QuestionnaireApiModels.FieldTemplate,
          | 'key'
          | 'listFieldTemplateId'
        >
      >;
    }
  >
};

const buildField = ({
  field,
  fieldsByKey,
  formMethods,
  responseSources,
  valueKeyPrefix,
}: {
  field: BaseFieldTemplate;
  fieldsByKey: Props['fieldsByKey'];
  formMethods: Props['formMethods'];
  responseSources: ResponseSources;
  valueKeyPrefix?: string;
}): Record<string, unknown> => {

  const thisField: Record<string, unknown> = {
    autofill: field.autofill,
    conditionExpression: field.conditionExpression,
    key: field.key,
    label: field.label,
    rank: field.rank,
    type: field.type,
    valueKeyPrefix,
  };

  if (field.listItemTemplates && field.listItemTemplates.length) {
    if (field.type === 'list' || field.type === 'table') {
      const rowValues = formMethods.getValues(field.key);
      if (Array.isArray(rowValues)) {
        thisField.rows = rowValues
          .map((rowValue, rowIndex) => {
            const columns = (field.listItemTemplates || [])
              .map(({ key }) => {
                const listItemTemplate = fieldsByKey[key];
                return {
                  [listItemTemplate.label]: buildField({
                    field: listItemTemplate,
                    fieldsByKey,
                    formMethods,
                    responseSources,
                    valueKeyPrefix: `${field.key}[${rowIndex}]`,
                  }),
                };
              });
            return { columns, id: rowValue._id };
          });
      } else {
        thisField.rows = [];
      }
    } else {
      const children: unknown[] = [];

      field.listItemTemplates.forEach(({ key }) => {
        const listItemTemplate = fieldsByKey[key];
        children.push({
          [listItemTemplate.label]: buildField({
            field: listItemTemplate,
            fieldsByKey,
            formMethods,
            responseSources,
            valueKeyPrefix,
          }),
        });
      });
      thisField.children = children;
    }
  } else {
    const fullFieldKey = valueKeyPrefix
      ? `${valueKeyPrefix}.${field.key}`
      : field.key;

    thisField.value = formMethods.getValues(fullFieldKey);
    thisField.source = responseSources[fullFieldKey];
  }

  return thisField;
};

const createResponseSummary = ({
  formMethods,
  responseSources,
  sections,
  fieldsByKey,
}: {
  formMethods: Props['formMethods'];
  responseSources: ResponseSources;
  sections: Props['sections'];
  fieldsByKey: Props['fieldsByKey'];
}): unknown => {
  const summary: unknown[] = sections
    .map(section => {
      type Subsection = {
        [key: string]: unknown[];
      };

      const subsectionIdxMap = new Map<string, number>();
      const subsections: Subsection[] = [];

      (section.subsections ?? []).forEach((subsection) => {
        subsectionIdxMap.set(subsection, subsections.length);
        subsections.push({
          [subsection]: [],
        });
      });

      section.fields
        .filter(field => !field.listFieldTemplateId)
        .forEach(({ key }) => {
          const field = fieldsByKey[key];
          if (!field) return;

          const subsection = field.subsection ?? '__NO_SUBSECTION__';
          let sectionIndex = subsectionIdxMap.get(subsection);
          if (sectionIndex === undefined) {
            sectionIndex = subsections.length;
            subsectionIdxMap.set(subsection, sectionIndex);
            subsections.push({
              [subsection]: [],
            });
          }
          subsections[sectionIndex][subsection].push({
            [field.label]: buildField({
              field,
              fieldsByKey,
              formMethods,
              responseSources,
            }),
          });
        });

      return {
        [section.label]: {
          key: section.key,
          rank: section.rank,
          subsections,
        },
      };
    });

  return summary;
};

const useQuestionnaireInspector = ({
  fieldsByKey,
  formMethods,
  responseSourcesRef,
  sections,
}: Props): void => {
  const dataRef = useRef<Omit<Props, 'responseSourcesRef'>>({
    fieldsByKey,
    formMethods,
    sections,
  });
  dataRef.current = {
    fieldsByKey,
    formMethods,
    sections,
  };

  useEffect(() => {
    window.questionnaireInspector = {
      showFieldsByKey: () => console.dir(dataRef.current.fieldsByKey, { depth: null}),
      showFormValues: () => console.dir(dataRef.current.formMethods.getValues(), { depth: null}),
      showResponses: () => {
        console.dir(createResponseSummary({
          fieldsByKey: dataRef.current.fieldsByKey,
          formMethods: dataRef.current.formMethods,
          responseSources: responseSourcesRef.current,
          sections: dataRef.current.sections,
        }), { depth: null});
      },
      showSources: () => console.dir(responseSourcesRef.current, { depth: null}),
    };

    window.evaluateExpressionProfiling = {
      disable: evaluateExpression.disableProfiling,
      enable: evaluateExpression.enableProfiling,
      log: () => {
        const { summary, countsById } = evaluateExpression.getProfilingData();
        console.log('evaluateExpression profiling', summary);
        Object.keys(countsById)
          .sort((a, b) => countsById[b] - countsById[a])
          .forEach((id) => {
            console.log(`  ${id}: ${countsById[id]}`);
          });
      },
    };

    window.eventsProfiling = {
      disable: trackEvents.disable,
      enable: trackEvents.enable,
      log: () => {
        const counts = trackEvents.getCounts();
        console.log('Render counts');
        Object.keys(counts)
          .sort((a, b) => counts[b] - counts[a])
          .forEach((id) => {
            console.log(`  ${id}: ${counts[id]}`);
          });
      },
    };
    return () => {
      evaluateExpression.disableProfiling();
      delete window.questionnaireInspector;
      delete window.evaluateExpressionProfiling;
      delete window.eventsProfiling;
    };
  }, [responseSourcesRef]);
};

export default useQuestionnaireInspector;